import { blobMediaUrl } from "@/lib/blob-url"
import type { Translator } from "@/lib/intl-types"
import type { BiomarkerLandingConfig } from "@/components/ui/biomarker-landing"

/**
 * PSA single-biomarker landing page content.
 *
 * Structure duplicated from /tests/lpa; all RENDERED copy is now real PSA copy,
 * sourced from the `go` i18n namespace (keys prefixed `psa*`). Grounded in Ahead's
 * own PSA biomarker + prostate-cancer insight pages and a cited deep-research pass
 * (EAU 2026, NCI, IQWiG): HWG-safe framing — PSA is a "triage signal, not a
 * diagnosis", most raised results are benign, ~1 in 4 biopsied men have cancer.
 * `en.json` is the source of truth; the other locales are EN stubs pending real
 * translation + review-cockpit sign-off (see /translation-workflow).
 *
 * The `explorer` (bands slider), `selfCheck` and `personas` blocks below are NOT
 * rendered by psa-client.tsx and still hold Lp(a) placeholder text — dead config,
 * harmless; delete or repurpose if those sections are ever mounted.
 *
 * STILL PLACEHOLDER: every image URL is an lpa-* asset (Tristan's call).
 * NEEDS CLINICAL / HWG SIGN-OFF: the illustrative ng/mL floating-card values, the
 * "Men 40+" stat, and the screening-age framing (EAU: 50 general, 45 family
 * history / African ancestry, 40 BRCA2).
 */
export function psaConfig(
  t: Translator<"go">,
  whatWeDetectHref: string,
  advancedBloodTestHref: string,
  priceFrom?: string
): BiomarkerLandingConfig {
  return {
    slug: "psa-blood-test",
    advancedBloodTestHref,

    hero: {
      eyebrow: t("psaHeroEyebrow"),
      badge: t("psaHeroBadge"),
      ctaNote: t("psaHeroCtaNote"),
      ctaNoteHref: advancedBloodTestHref,
      heading: t("psaHeroHeading"),
      body: t("psaHeroBody"),
      primaryCta: t("psaHeroPrimaryCta"),
      secondaryCta: t("psaHeroSecondaryCta"),
      secondaryHref: "#what-it-is",
      // No `rating` here on purpose. The Lp(a) page carries a literal
      // `{ value: "5.0", label: "· 34 reviews" }` that this page was copied
      // from; it is unsourced, and PSA has not launched, so there is nothing
      // to source it from. The field is optional and currently unrendered, so
      // dropping it changes nothing on screen — it just stops a fabricated
      // figure being one wire-up away from going live.
      priceFrom,
      image: {
        src: blobMediaUrl("/images/marketing/psa-hero-window.jpeg"),
        alt: "Man in his fifties looking out of a window in warm morning light",
        position: "center",
      },
      // Floating result cards tell the core story at a glance: a single PSA is
      // not definitive, so tracking the same marker over time is what matters.
      // NEEDS CLINICAL SIGN-OFF: the ng/mL values are illustrative only.
      floatingCards: [
        {
          name: t("psaHeroCardCholesterolName"),
          value: "1.1 ng/mL",
          status: t("psaHeroCardInRange"),
          tone: "good",
        },
        {
          name: "PSA · 2025",
          value: "3.9 ng/mL",
          status: t("psaHeroCardElevated"),
          tone: "bad",
        },
      ],
    },

    // Test-detail info card (mirrors the /tests/<slug> quick-facts block).
    // NEEDS CLINICAL SIGN-OFF on the prep window and "for whom" claims.
    detailCard: {
      heading: t("psaDetailHeading"),
      benefits: [
        t("psaDetailBenefit1"),
        t("psaDetailBenefit2"),
        t("psaDetailBenefit3"),
        t("psaDetailBenefit4"),
      ],
      primaryCta: t("psaHeroPrimaryCta"),
      secondaryCta: {
        label: t("psaDetailSecondaryCta"),
        href: advancedBloodTestHref,
      },
      rows: [
        {
          icon: "healthAreas",
          label: t("psaDetailRow1Label"),
          value: t("psaDetailRow1Value"),
        },
        {
          icon: "time",
          label: t("psaDetailRow2Label"),
          value: t("psaDetailRow2Value"),
        },
        {
          icon: "radiation",
          label: t("psaDetailRow3Label"),
          value: t("psaDetailRow3Value"),
        },
        {
          icon: "contrast",
          label: t("psaDetailRow4Label"),
          value: t("psaDetailRow4Value"),
        },
        {
          icon: "preparation",
          label: t("psaDetailRow5Label"),
          value: t("psaDetailRow5Value"),
        },
        {
          icon: "forWhom",
          label: t("psaDetailRow6Label"),
          value: t("psaDetailRow6Value"),
        },
      ],
    },

    facts: {
      eyebrow: t("psaFactsEyebrow"),
      heading: t("psaFactsHeading"),
      headlineStat: {
        value: t("psaFactsStatValue"),
        caption: t("psaFactsStatCaption"),
      },
      // Four audience cards from the "Who is it for?" copy (psaFact4 = family history).
      items: [
        { title: t("psaFact1Title"), body: t("psaFact1Body"), icon: "users" },
        { title: t("psaFact4Title"), body: t("psaFact4Body"), icon: "dna" },
        {
          title: t("psaFact2Title"),
          body: t("psaFact2Body"),
          icon: "warning",
        },
        { title: t("psaFact3Title"), body: t("psaFact3Body"), icon: "shield" },
      ],
    },

    gap: {
      eyebrow: t("psaGapEyebrow"),
      heading: t("psaGapHeading"),
      body: t("psaGapBody"),
      image: {
        src: blobMediaUrl("/images/homepage/age-40s.jpg"),
        alt: "Healthy-looking person in their forties",
        position: "center",
      },
      // Auto-scrolling biomarker marquee (reused from /report). A standard panel
      // reads clean across the board while PSA is never measured (greyed).
      // Values/units are illustrative literals; labels + statuses are i18n.
      marquee: [
        {
          label: t("psaMarker1"),
          value: "4.8",
          unit: "mmol/L",
          status: t("psaStatusNormal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 30, color: "#ed9062" },
            { h: 45, color: "#408c49", active: true },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 45,
          ghosts: [50, 42],
        },
        {
          label: t("psaMarker2"),
          value: "1.6",
          unit: "mmol/L",
          status: t("psaStatusOptimal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 50, color: "#408c49", active: true },
            { h: 25, color: "#c8b59d" },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 22,
          ghosts: [28, 25],
        },
        {
          label: t("psaMarker3"),
          value: "1.8",
          unit: "mmol/L",
          status: t("psaStatusNormal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 30, color: "#ed9062" },
            { h: 45, color: "#408c49", active: true },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 48,
          ghosts: [52, 46],
        },
        {
          label: t("psaMarker4"),
          value: "—",
          unit: "ng/mL",
          status: t("psaStatusNotMeasured"),
          statusBg: "",
          statusText: "",
          segments: [
            { h: 33, color: "#e5e5e5" },
            { h: 34, color: "#e5e5e5" },
            { h: 33, color: "#e5e5e5" },
          ],
          dotY: 50,
          ghosts: [],
          muted: true,
        },
      ],
    },

    explorer: {
      eyebrow: t("psaExplorerEyebrow"),
      heading: t("psaExplorerHeading"),
      caption: t("psaExplorerCaption"),
      unit: "ng/mL",
      // Thresholds follow EAU practice: 3 ng/mL triggers a closer look, 10 is
      // clearly raised. Capped at 12 (not 20) so "Clearly raised" reads as a
      // slim top segment rather than half the track. The slider steps in whole
      // ng/mL, so every boundary here has to stay an integer.
      sliderMax: 12,
      defaultValue: 1,
      bands: [
        {
          label: t("psaBand1Label"),
          tone: "good",
          from: 0,
          to: 3,
          status: t("psaBand1Status"),
          level: 1,
          meaning: t("psaBand1Meaning"),
        },
        {
          label: t("psaBand2Label"),
          tone: "warn",
          from: 3,
          to: 10,
          status: t("psaBand2Status"),
          level: 2,
          meaning: t("psaBand2Meaning"),
        },
        {
          label: t("psaBand3Label"),
          tone: "bad",
          from: 10,
          to: 12,
          status: t("psaBand3Status"),
          level: 3,
          meaning: t("psaBand3Meaning"),
        },
      ],
      factorsLabel: t("psaExplorerFactorsLabel"),
      factors: [
        t("psaFactor1"),
        t("psaFactor2"),
        t("psaFactor3"),
        t("psaFactor4"),
      ],
    },

    risks: {
      eyebrow: t("psaRisksEyebrow"),
      heading: t("psaRisksHeading"),
      body: t("psaRisksBody"),
      seeAllLabel: t("psaRisksSeeAll"),
      seeAllHref: whatWeDetectHref,
      items: [
        { title: t("psaRisk1Title"), body: t("psaRisk1Body"), icon: "warning" },
        { title: t("psaRisk2Title"), body: t("psaRisk2Body"), icon: "enlarge" },
        {
          title: t("psaRisk3Title"),
          body: t("psaRisk3Body"),
          icon: "inflammation",
        },
        {
          title: t("psaRisk4Title"),
          body: t("psaRisk4Body"),
          icon: "spike",
        },
      ],
    },

    selfCheck: {
      eyebrow: t("psaSelfCheckEyebrow"),
      heading: t("psaSelfCheckHeading"),
      intro: t("psaSelfCheckIntro"),
      questions: [
        { id: "family", label: t("psaSelfCheckQ1") },
        { id: "age", label: t("psaSelfCheckQ2") },
        { id: "symptoms", label: t("psaSelfCheckQ3") },
        { id: "never-measured", label: t("psaSelfCheckQ4") },
      ],
      results: [
        {
          minChecks: 0,
          tone: "good",
          title: t("psaSelfCheckResult1Title"),
          body: t("psaSelfCheckResult1Body"),
        },
        {
          minChecks: 1,
          tone: "warn",
          title: t("psaSelfCheckResult2Title"),
          body: t("psaSelfCheckResult2Body"),
        },
        {
          minChecks: 2,
          tone: "bad",
          title: t("psaSelfCheckResult3Title"),
          body: t("psaSelfCheckResult3Body"),
        },
      ],
      primaryCta: t("psaSelfCheckCta"),
    },

    action: {
      eyebrow: t("psaActionEyebrow"),
      heading: t("psaActionHeading"),
      body: t("psaActionBody"),
      items: [
        {
          title: t("psaAction1Title"),
          body: t("psaAction1Body"),
          icon: "refresh",
        },
        {
          title: t("psaAction2Title"),
          body: t("psaAction2Body"),
          icon: "stethoscope",
        },
        {
          title: t("psaAction3Title"),
          body: t("psaAction3Body"),
          icon: "checklist",
        },
        {
          title: t("psaAction4Title"),
          body: t("psaAction4Body"),
          icon: "users",
        },
      ],
      image: {
        src: blobMediaUrl("/images/marketing/psa-action-street.jpg"),
        alt: "Man walking down a city street in warm golden light",
        // Anchor high so the man's head isn't cropped by object-cover.
        position: "50% 20%",
      },
    },

    // Unused (the live TestimonialsSection replaces this block) — kept English.
    personas: {
      eyebrow: "Real-world reasons",
      heading: "People who decided to know their number",
      note: "Illustrative profiles, not testimonials.",
      items: [
        {
          name: "David, 45",
          scenario:
            "His father had a heart attack at 52. David's cholesterol was normal, his Lp(a) was not.",
          photo: {
            src: blobMediaUrl("/images/report/person-david.jpg"),
            alt: "Portrait of a man in his forties",
          },
        },
        {
          name: "Eva, 38",
          scenario:
            "She wanted the full picture of her heart risk before starting a family.",
          photo: {
            src: blobMediaUrl("/images/report/person-eva.jpg"),
            alt: "Portrait of a woman in her late thirties",
          },
        },
        {
          name: "Lena, 48",
          scenario:
            "Heart disease runs in her family, even though everyone's cholesterol looks fine.",
          photo: {
            src: blobMediaUrl("/images/report/person-lena.jpg"),
            alt: "Portrait of a woman in her late forties",
          },
        },
      ],
    },

    biomarkerPanel: {
      eyebrow: t("psaPanelEyebrow"),
      heading: t("psaPanelHeading"),
      subheading: t("psaPanelSubheading"),
      ctaLabel: t("psaPanelCta"),
    },

    steps: {
      eyebrow: t("psaStepsEyebrow"),
      heading: t("psaStepsHeading"),
      body: t("psaStepsBody"),
      stepWord: t("psaStepsStepWord"),
      sampleReportCta: t("psaStepsSampleReport"),
      locationsCta: t("psaStepsLocationsCta"),
      items: [
        {
          title: t("psaStep1Title"),
          body: t("psaStep1Body"),
          image: {
            src: blobMediaUrl("/images/marketing/psa-step1-booked.jpg"),
            alt: "Man at home by a window with a laptop on the desk",
          },
        },
        {
          title: t("psaStep2Title"),
          body: t("psaStep2Body"),
          image: {
            src: blobMediaUrl("/images/marketing/psa-step2-blood-draw.jpeg"),
            alt: "Clinician taking a blood sample from a man in a clinic",
            // Bias the crop toward the faces and the draw.
            position: "50% 40%",
          },
        },
        {
          title: t("psaStep3Title"),
          body: t("psaStep3Body"),
          image: {
            src: blobMediaUrl("/images/marketing/lpa-step3-report.jpeg"),
            alt: "Tablet showing a health report on a kitchen counter",
          },
        },
      ],
    },

    faq: {
      heading: t("psaFaqHeading"),
      // Fallback only — the live FAQ comes from the CMS (Advanced blood test set).
      items: [
        {
          q: "Is PSA included in a standard blood test?",
          a: "Not usually. PSA has to be requested specifically, which is why many men have never had it measured.",
        },
        {
          q: "Does a high PSA mean I have cancer?",
          a: "No. PSA is a triage signal, not a diagnosis. Benign enlargement, inflammation, or even recent cycling or ejaculation can raise it, and only about 1 in 4 men who go on to a biopsy are found to have cancer.",
        },
        {
          q: "How should I prepare?",
          a: "Avoid vigorous exercise and sexual activity for 48 hours before the test, and postpone if you have a urinary infection.",
        },
        {
          q: "How often should I test?",
          a: "Commonly every 1 to 2 years from age 45, or from 40 with a family history of prostate cancer. Your doctor tailors this to you.",
        },
        {
          q: "What happens if my PSA is raised?",
          a: "Usually a repeat test and an MRI scan before any biopsy. Read alongside imaging, many men avoid an unnecessary biopsy.",
        },
        {
          q: "Should everyone get a PSA test?",
          a: "No. Testing is a personal choice, best made with your doctor. PSA has real benefits, but also downsides: some raised results lead to further tests, and screening can find slow-growing cancers that may never cause harm. We help you weigh it up and decide.",
        },
      ],
    },

    closing: {
      eyebrow: t("psaClosingEyebrow"),
      heading: t("psaClosingHeading"),
      body: t("psaClosingBody"),
      primaryCta: t("psaClosingPrimaryCta"),
      secondaryCta: t("psaClosingSecondaryCta"),
      secondaryHref: "#what-it-is",
      image: {
        src: blobMediaUrl("/images/marketing/psa-closing-trail.jpeg"),
        alt: "Hiker with a backpack walking a mountain trail toward the Alps",
        position: "center",
      },
    },
  }
}
